import { ListFilter } from '@app/components/ListFilter';
import { Button } from '@app/components/various/Button';
import { SearchTopBar } from '@app/components/various/SearchTopBar';
import { Status } from '@app/components/various/Status';
import { TableControls } from '@app/components/various/TableControls';
import { useAmplitude } from '@app/utils/analytics/useAmplitude';
import { useModalVisibility } from '@app/utils/hooks';
import { useOrderStatusList } from '@app/utils/hooks/useOrderStatusList';
import { isEmpty } from '@app/utils/isEmpty';
import { Table } from '@tanstack/react-table';
import { ColumnsConfiguration, IconComponentProps, Order } from '@typings';
import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  table: Table<Order.Essentials>;
  search: string;
  status: Order.SelectionStatus | Order.OrderStatus | '';
  totalCount?: number;
  columnsVisibility: ColumnsConfiguration;
  onSearchChange: (value: string) => void;
  onStatusChange: (status: Order.SelectionStatus) => void;
}

export const SelectionsListTopBar = ({ table, search, status, totalCount, columnsVisibility, onSearchChange, onStatusChange }: Props) => {
  const { t } = useTranslation(['orders', 'selections']);

  const { editable: editableStages } = useOrderStatusList();
  const { trackEvent } = useAmplitude();
  const { showModal } = useModalVisibility('CreateOrderModal');

  const filters = editableStages.map(stage => ({
    icon: ({ className }: IconComponentProps) => (
      <span className={cx(className, 'flex items-center justify-center')}>{!isEmpty(stage.value) && <Status type={stage.value} />}</span>
    ),
    ...stage,
  }));

  const handleSearchBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (!isEmpty(event.target.value)) {
      trackEvent({ name: 'selectionsTable.input.search.filledin' });
    }
  };

  const handleControlsChange = (column: string, isChecked: boolean) => {
    if (!isChecked) {
      trackEvent({ name: 'selectionsTable.multiselect.column.unchecked', properties: { column } });
    }
  };

  const handleSelectionTypeChange = (selectionType: Order.SelectionStatus) => {
    onStatusChange(selectionType);
    trackEvent({ name: 'selectionsTable.radioButton.status.change' });
  };

  return (
    <SearchTopBar
      title={t('selections:selection_other')}
      totalCount={totalCount}
      search={search}
      searchPlaceholder={t('selections:search_selection')}
      postfix={
        <Button onClick={showModal} dataTestId="createSelectionButton">
          {t('selections:create_selection')}
        </Button>
      }
      onSearchBlur={handleSearchBlur}
      onSearchChange={onSearchChange}
    >
      <ListFilter title={t('selections:choose_selection_type')} onChange={handleSelectionTypeChange} selected={status} filters={filters} />
      <TableControls table={table} columnsConfiguration={columnsVisibility} tableName="selectionsTable" onChange={handleControlsChange} />
    </SearchTopBar>
  );
};
